import { UserPlusIcon } from "@heroicons/react/24/solid";
import { Button } from "@moe/oss/ui/button";
import { Profile } from "@moe/priv/model/profile";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AuthModal } from "@web/components/AuthModal";
import { useAppContext } from "@web/route-services/root/AppContext";
import { toast } from "sonner";

interface Props {
  profile: Profile;
}

export function FollowButton({ profile }: Props) {
  const { access, session, createModal } = useAppContext();
  const queryClient = useQueryClient();

  const toggleFollowMutation = useMutation({
    mutationFn: async (profile: Profile) => {
      if (profile.isFollowing) {
        await access.profile.unfollow({ id: profile.id });
      } else {
        await access.profile.follow({ id: profile.id });
      }
    },
    onMutate: async (profile: Profile) => {
      await queryClient.cancelQueries({ queryKey: ["profile", profile.username] });
      const previousProfile = queryClient.getQueryData(["profile", profile.username]);
      queryClient.setQueryData(["profile", profile.username], (old: any) => ({
        ...old,
        isFollowing: !profile.isFollowing
      }));

      return { previousProfile };
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(["profile", profile.username], context?.previousProfile);
      toast.error("Failed to toggle follow");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", profile.username] });
    }
  });
  const followToggleHandler = () => {
    if (!session) {
      createModal({ content: <AuthModal formKind="signup" /> });
      return;
    }
    if (!profile) return;
    toggleFollowMutation.mutate(profile);
  };

  return (
    <Button className="flex items-center justify-center space-x-2 rounded-lg" onClick={followToggleHandler}>
      <UserPlusIcon />
      {profile.isFollowing ? "Following" : "Follow"}
    </Button>
  );
}
